import { useAuthStore } from '../stores/auth.store';

// Single source of truth: URL base dell'API con prefix versione.
// Esportato perché alcuni file (download raw, signing page pubblica) hanno
// bisogno di fare fetch fuori dal wrapper `apiFetch` (es. per blob download).
export const API_BASE_URL: string = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000') + '/api/v1';
const BASE_URL = API_BASE_URL;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

type FetchOpts = RequestInit & { auth?: boolean };

let refreshInflight: Promise<string | null> | null = null;

/**
 * Un rinnovo del token alla volta per tutta l'app. Esportata il 29/10/2026 per il lettore
 * del flusso (leggiFlusso, sotto): deve usare questa e mai una copia, perché due rinnovi
 * in parallelo con lo stesso cookie fanno scattare il controllo del riuso
 * (auth.service.ts), che chiude tutte le sessioni della persona.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      useAuthStore.getState().setAuth(data.user, data.accessToken);
      return data.accessToken as string;
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const { auth = true, headers, ...rest } = opts;
  const token = useAuthStore.getState().accessToken;

  const doFetch = async (t: string | null) => {
    const finalHeaders: Record<string, string> = {
      ...(headers as Record<string, string> | undefined),
    };
    // Set Content-Type ONLY if there's a body (Fastify complains on empty JSON body). Un FormData
    // no (i vocali della 2G, 29/10/2026): il Content-Type col boundary lo scrive fetch.
    if (rest.body !== undefined && rest.body !== null && !(rest.body instanceof FormData) && !finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
    if (auth && t) finalHeaders['Authorization'] = `Bearer ${t}`;
    try {
      const res = await fetch(`${BASE_URL}${path}`, { ...rest, credentials: 'include', headers: finalHeaders });
      // API ferma dietro nginx: la mascotte apre l'ombrello (features/assistente/mascotteVita.ts).
      // Non per /ai: lì il 502 vuol dire che non ha risposto Claude, l'API c'è, e il
      // pannello lo dice già (28/10/2026).
      if (res.status >= 502 && res.status <= 504 && !path.startsWith('/ai/')) window.dispatchEvent(new Event('msm:rete-giu'));
      return res;
    } catch (err) {
      // La rete che cade: stesso segnale, poi l'errore va avanti come prima.
      window.dispatchEvent(new Event('msm:rete-giu'));
      throw err;
    }
  };

  let res = await doFetch(token);

  if (res.status === 401 && auth) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doFetch(newToken);
    } else {
      useAuthStore.getState().clear();
      throw new ApiError(401, 'UNAUTHORIZED', 'Sessione scaduta: accedi di nuovo');
    }
  }

  if (!res.ok) throw await erroreDa(res);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * L'errore di una risposta non ok, per apiFetch e leggiFlusso (in comune dal 29/10/2026).
 * Ogni risposta d'errore dell'API ha la stessa forma, quella di Fastify:
 * { statusCode, code, error, message, details? } (plugins/error-handler.ts).
 * `message` è già la frase in italiano da mostrare; `error` è solo il nome
 * inglese dello stato ("Bad Request") e non va mai nei toast. Senza un corpo
 * leggibile (l'API ferma dietro nginx, la rete che cade a metà) resta una
 * frase generica, non il testo del protocollo.
 */
async function erroreDa(res: Response): Promise<ApiError> {
  let payload: any = null;
  try { payload = await res.json(); } catch {}
  return new ApiError(
    res.status,
    typeof payload?.code === 'string' ? payload.code : 'ERROR',
    typeof payload?.message === 'string' && payload.message ? payload.message : 'Qualcosa è andato storto, riprova tra poco',
    payload?.details,
  );
}

export const api = {
  get: <T = unknown>(path: string, opts?: FetchOpts) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T = unknown>(path: string, opts?: FetchOpts) => apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};

// ── Il flusso di MUNE (text/event-stream), fetta 2A della Fase 2 (os-planning/ai/fase-2-progetto.md) ──

/** Una risposta ok che non è un flusso: un proxy, una pagina d'errore, un'API di prima. */
const FLUSSO_NON_VALIDO = 'MUNE non ha risposto bene: riprova';

/**
 * La rete che cade (fetch o lettura che lanciano un TypeError) manda «msm:rete-giu» come
 * apiFetch, e la mascotte in alto apre l'ombrello. Mai per un'interruzione voluta col
 * signal (AbortError): non è un guasto (29/10/2026).
 */
function segnalaReteGiu(err: unknown, signal: AbortSignal | undefined) {
  if (err instanceof TypeError && !signal?.aborted) window.dispatchEvent(new Event('msm:rete-giu'));
}

/**
 * Legge una risposta in streaming dell'API e passa a `onEvento` ogni evento intero, col nome
 * della riga `event:` e i dati della riga `data:` già letti dal JSON (29/10/2026, fetta 2A).
 * Non EventSource: fa solo GET e non manda Authorization.
 *
 * Come apiFetch: Bearer della sessione e `credentials: 'include'`; un 401 rinnova il token
 * con refreshAccessToken (uno alla volta) e riprova una volta, se no chiude la sessione e
 * lancia ApiError 401; gli altri errori arrivano prima del flusso, in JSON (400, 402, 403,
 * 409, 429, 503), e diventano l'ApiError con la frase dell'API. Una risposta ok che non è
 * text/event-stream lancia ApiError(502, 'FLUSSO', «MUNE non ha risposto bene: riprova»).
 *
 * Eventi separati da una riga vuota; le righe che cominciano con «:» sono i commenti del
 * server (`: inizio`, `: attesa` ogni 15 secondi) e si saltano. Un evento lasciato a metà
 * da un flusso che si chiude non arriva, come in EventSource. La promessa si risolve
 * quando il server chiude il flusso: se è arrivato l'evento finale lo sa chi legge.
 *
 * Interrotta col `signal` (Nuova conversazione, un'altra conversazione aperta) chiude la
 * richiesta e finisce in silenzio. La rete che cade manda «msm:rete-giu» e poi lancia
 * l'errore; un 502-504 di /ai/ no, perché lì vuol dire che non ha risposto Claude. Un
 * errore lanciato da `onEvento` chiude la richiesta ed esce così com'è.
 */
export async function leggiFlusso(path: string, opts: {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
  onEvento: (nome: string, dati: unknown) => void;
}): Promise<void> {
  const { method = 'GET', body, signal, onEvento } = opts;

  const apri = async (t: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (t) headers['Authorization'] = `Bearer ${t}`;
    let res: Response;
    try {
      res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include',
        signal,
      });
    } catch (err) {
      segnalaReteGiu(err, signal);
      throw err;
    }
    // Come apiFetch: l'API ferma dietro nginx apre l'ombrello, ma non per /ai/.
    if (res.status >= 502 && res.status <= 504 && !path.startsWith('/ai/')) window.dispatchEvent(new Event('msm:rete-giu'));
    return res;
  };

  try {
    let res = await apri(useAuthStore.getState().accessToken);
    if (res.status === 401) {
      // Il 401 nasce prima del gestore della rotta: sul server non è partito niente, si riprova.
      res.body?.cancel().catch(() => {});
      const nuovo = await refreshAccessToken();
      if (!nuovo) {
        useAuthStore.getState().clear();
        throw new ApiError(401, 'UNAUTHORIZED', 'Sessione scaduta: accedi di nuovo');
      }
      res = await apri(nuovo);
    }
    if (!res.ok) throw await erroreDa(res);
    const tipo = (res.headers.get('content-type') ?? '').toLowerCase();
    if (!tipo.startsWith('text/event-stream') || !res.body) {
      res.body?.cancel().catch(() => {});
      throw new ApiError(502, 'FLUSSO', FLUSSO_NON_VALIDO);
    }
    await leggiEventi(res.body, signal, onEvento);
  } catch (err) {
    // Interrotta da chi l'ha chiesta: niente errore e niente ombrello.
    if (signal?.aborted) return;
    throw err;
  }
}

/**
 * Le righe del flusso, un pezzo di rete alla volta: TextDecoder con { stream: true }, perché
 * una lettera accentata può arrivare spezzata tra due pezzi; righe chiuse da LF, CRLF o CR,
 * come vuole lo standard degli eventi del server. Si ferma appena il signal scatta, anche a
 * metà di un pezzo (un `onEvento` che interrompe non riceve gli eventi dopo).
 */
async function leggiEventi(
  corpo: ReadableStream<Uint8Array>,
  signal: AbortSignal | undefined,
  onEvento: (nome: string, dati: unknown) => void,
): Promise<void> {
  const reader = corpo.getReader();
  const decoder = new TextDecoder();
  let resto = '';            // il testo dopo l'ultimo a capo: una riga ancora a metà
  let nome = '';
  let dati: string[] = [];
  let letto = false;

  const riga = (r: string) => {
    if (r === '') {
      // Riga vuota: l'evento è intero. Senza `data:` non c'è niente da passare.
      if (dati.length > 0) {
        let valore: unknown;
        try {
          valore = JSON.parse(dati.join('\n'));
        } catch {
          throw new ApiError(502, 'FLUSSO', FLUSSO_NON_VALIDO);
        }
        onEvento(nome || 'message', valore);
      }
      nome = '';
      dati = [];
      return;
    }
    if (r.startsWith(':')) return;
    const i = r.indexOf(':');
    const campo = i < 0 ? r : r.slice(0, i);
    const valore = i < 0 ? '' : r.slice(i + 1).replace(/^ /, '');
    if (campo === 'event') nome = valore;
    else if (campo === 'data') dati.push(valore);
    // `id` e `retry` non servono: nessuna riconnessione automatica.
  };

  const righeIntere = (finito: boolean) => {
    // Una CR in fondo può essere la prima metà di un CRLF: si aspetta il pezzo dopo.
    const tieni = !finito && resto.endsWith('\r');
    const parti = (tieni ? resto.slice(0, -1) : resto).split(/\r\n|\r|\n/);
    resto = (parti.pop() ?? '') + (tieni ? '\r' : '');
    for (const r of parti) {
      if (signal?.aborted) return;
      riga(r);
    }
  };

  try {
    for (;;) {
      let pezzo: ReadableStreamReadResult<Uint8Array>;
      try {
        pezzo = await reader.read();
      } catch (err) {
        segnalaReteGiu(err, signal);
        throw err;
      }
      if (pezzo.done) break;
      resto += decoder.decode(pezzo.value, { stream: true });
      righeIntere(false);
    }
    resto += decoder.decode();
    righeIntere(true);
    // Quello che resta (una riga senza a capo, un evento senza la riga vuota) non è intero.
    letto = true;
  } finally {
    if (!letto) reader.cancel().catch(() => {});
  }
}
